/*
  Rationale: This heavy Three.js module lives in the theme's src bundle and is
  lazy-loaded only when the block is present. The block-level view.js remains
  a tiny initializer (declared via block.json viewScript) that calls into the
  theme (window.wsInitModel3DSectionIn), which imports this module on demand.
  This keeps editor previews fast and avoids shipping Three.js in every block script.
*/
import Swiper from 'swiper';
import { Autoplay, Pagination } from 'swiper/modules';
import * as THREE from 'three';
import { OutlineEffect } from 'three/examples/jsm/effects/OutlineEffect.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

export function initModel3DSection(rootEl) {
  if (!rootEl) return;

  let camera, scene, renderer, effect;
  let canvasRoot = null;
  let isAnimating = false; // start paused; resume only when in view
  let hasInitialized = false;
  let visibilityState = false;
  // Swiper v11 modules are provided via the `modules` option
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();
  let meshes = [];
  let modelRef = null;
  let modelCenter = new THREE.Vector3(0,0,0);
  let modelRadius = 1;
  let autoRotateSpeed = 0.002;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const inView = entry.isIntersecting && entry.intersectionRatio > 0.1;
      if (!hasInitialized && inView) {
        init();
        hasInitialized = true;
        setVisible(true);
      } else if (hasInitialized) {
        setVisible(inView);
      }
    });
  }, { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] });
  observer.observe(rootEl);

  function init() {
    canvasRoot = rootEl.querySelector('.js-model3d-section__canvas') || rootEl;
    const width = canvasRoot.clientWidth || 800;
    const height = canvasRoot.clientHeight || 500;

    camera = new THREE.PerspectiveCamera(45, width / height, 0.25, 50);
    camera.position.set(-1.8, 20, 2.7);

    scene = new THREE.Scene();
    scene.add(new THREE.AmbientLight(0xffffff));
    const pointLight = new THREE.PointLight(0xffffff, 100);
    camera.add(pointLight);
    scene.add(camera);

    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(width, height);
    canvasRoot.appendChild(renderer.domElement);
    // Smooth fade for canvas element
    try {
      renderer.domElement.style.opacity = '0';
      renderer.domElement.style.transition = 'opacity 300ms ease';
    } catch (e) {}

    effect = new OutlineEffect(renderer);

    // Controls intentionally disabled to avoid user interactions (scroll/drag)

    const loader = new GLTFLoader();
    // Fallback to original theme asset if no override provided
    const modelUrl = (window.wsModel3D && window.wsModel3D.url) || '/wp-content/themes/ws-theme/blocks/model3d-section/ws2.gltf';
    if (!modelUrl) {
      // No model configured; just render empty scene
      render();
      bindEvents();
      return;
    }

    loader.load(modelUrl, async (gltf) => {
      const model = gltf.scene;
      await renderer.compileAsync(model, camera, scene);
      model.traverse((child) => {
        if (child.isMesh) {
          meshes.push(child);
          child.material = new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.8 });
          const edges = new THREE.EdgesGeometry(child.geometry, 10);
          const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0xffffff }));
          child.add(line);
        }
      });

      scene.add(model);
      model.scale.setScalar(1);
      model.scale.y = 3;
      model.rotation.x = -0.75;
      model.rotation.y = -1;
      modelRef = model;
      fitCameraToModel(camera, renderer, model);

      animateCameraSequence(camera);
      // Kick render only when visible; observer will handle visibility
      if (visibilityState) {
        startRendering();
      }
      bindEvents();
      // Initialize Swiper once model is ready
      initSwiper();
    });
  }

  function bindEvents() {
    window.addEventListener('resize', onResize);
  }

  function onResize() {
    const width = (canvasRoot && canvasRoot.clientWidth) || rootEl.clientWidth || 800;
    const height = (canvasRoot && canvasRoot.clientHeight) || rootEl.clientHeight || 500;
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height);
    if (modelRef) fitCameraToModel(camera, renderer, modelRef, { maintainAngle: true });
    // Guard legacy helper if present in global scope
    try { if (typeof syncItemsHeight === 'function') syncItemsHeight(); } catch (e) {}
  }

  // Hover highlighting disabled per requirements

  function render() {
    if (modelRef) {
      modelRef.rotation.y += autoRotateSpeed;
    }
    effect.render(scene, camera);
    if (isAnimating) requestAnimationFrame(render);
  }

  function startRendering() {
    if (!isAnimating) {
      isAnimating = true;
      try { if (renderer && renderer.domElement) renderer.domElement.style.opacity = '1'; } catch (e) {}
      requestAnimationFrame(render);
      controlSwiper('start');
    }
  }

  function stopRendering() {
    if (isAnimating) {
      isAnimating = false;
      try { if (renderer && renderer.domElement) renderer.domElement.style.opacity = '0'; } catch (e) {}
      controlSwiper('stop');
    }
  }

  function setVisible(inView) {
    visibilityState = !!inView;
    if (!hasInitialized) return;
    if (visibilityState) startRendering(); else stopRendering();
  }

  function animateCameraSequence(cam) {
    const baseDirs = [
      new THREE.Vector3(0, 1, 0.3),
      new THREE.Vector3(0.25, 0.85, 0.4),
      new THREE.Vector3(-0.8, 0.8, 0.2),
      new THREE.Vector3(-0.9, 0.3, -0.5),
      new THREE.Vector3(-0.2, 1.2, -0.4)
    ].map(v => v.normalize());
    let i = 0;
    function step() {
      i = (i + 1) % baseDirs.length;
      const required = getRequiredDistance(camera, renderer, modelRadius);
      const next = modelCenter.clone().add(baseDirs[i].clone().multiplyScalar(required));
      // simple lerp without gsap
      const durationMs = 3000;
      const start = performance.now();
      const startPos = cam.position.clone();
      const tick = (t) => {
        const k = Math.min(1, (t - start) / durationMs);
        cam.position.lerpVectors(startPos, next, k);
        cam.lookAt(modelCenter);
        if (k < 1) requestAnimationFrame(tick); else {
          colorNextMesh();
          changeActiveItem();
          setTimeout(step, 3000);
        }
      };
      requestAnimationFrame(tick);
    }
    step();
  }

  function colorNextMesh() {
    if (!meshes.length) return;
    // store static index
    if (typeof colorNextMesh.index === 'undefined') colorNextMesh.index = 0;
    const prev = meshes[colorNextMesh.index];
    if (prev) {
      if (prev.currentHex !== undefined) {
        fadeMeshColor(prev, prev.material.color.getHex(), prev.currentHex, 400);
      }
      prev.isAnimating = false;
    }
    // advance index and skip some indices like original (optional)
    colorNextMesh.index = (colorNextMesh.index + 1) % meshes.length;
    if ([0,3,6].includes(colorNextMesh.index)) colorNextMesh.index = (colorNextMesh.index + 1) % meshes.length;
    const curr = meshes[colorNextMesh.index];
    if (curr) {
      curr.currentHex = curr.material.color.getHex();
      fadeMeshColor(curr, curr.currentHex, 0xffffff, 400);
      curr.isAnimating = true;
    }
  }

  function changeActiveItem() {
    const wrapper = rootEl.closest('.model3d-section') || document;
    const container = wrapper.querySelector('.model3d-section__items.swiper');
    if (!container || !container.swiper) return;
    container.swiper.slideNext();
  }

  function initSwiper() {
    const wrapper = rootEl.closest('.model3d-section') || document;
    const container = wrapper.querySelector('.model3d-section__items.swiper');
    if (!container) return;

    const swiper = new Swiper(container, {
      modules: [Autoplay, Pagination],
      direction: 'vertical',
      slidesPerView: 1,
      spaceBetween: 0,
      loop: true,
      allowTouchMove: false,
      speed: 600,
      autoHeight: false,
      autoplay: {
        delay: 3000,
        disableOnInteraction: false
      },
      pagination: {
        el: wrapper.querySelector('.model3d-section__progress'),
        type: 'progressbar',
        clickable: false
      },
      on: {
        afterInit: function(){ try { if (window.wsLocoUpdate) window.wsLocoUpdate(); } catch (e) {} },
        slideChange: function(){ try { if (window.wsLocoUpdate) window.wsLocoUpdate(); } catch (e) {} },
        resize: function(){ try { if (window.wsLocoUpdate) window.wsLocoUpdate(); } catch (e) {} },
        imagesReady: function(){ try { if (window.wsLocoUpdate) window.wsLocoUpdate(); } catch (e) {} }
      }
    });

    // Store reference for external control
    container.swiper = swiper;

    // Keep for potential external syncs in future
    try { if (window.wsLocoUpdate) window.wsLocoUpdate(); } catch (e) {}
  }

  function controlSwiper(action) {
    try {
      const wrapper = rootEl.closest('.model3d-section') || document;
      const container = wrapper.querySelector('.model3d-section__items.swiper');
      const swiper = container && container.swiper;
      if (!swiper || !swiper.autoplay) return;
      if (action === 'start') {
        if (typeof swiper.autoplay.start === 'function') swiper.autoplay.start();
      } else if (action === 'stop') {
        if (typeof swiper.autoplay.stop === 'function') swiper.autoplay.stop();
      }
    } catch (e) {}
  }

  // Utilities
  function fitCameraToModel(cam, rend, model, opts) {
    const width = (canvasRoot && canvasRoot.clientWidth) || rootEl.clientWidth || 800;
    const height = (canvasRoot && canvasRoot.clientHeight) || rootEl.clientHeight || 500;
    const box = new THREE.Box3().setFromObject(model);
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    modelCenter.copy(sphere.center);
    modelRadius = Math.max(sphere.radius, 0.001);

    const required = getRequiredDistance(cam, rend, modelRadius);
    // optionally keep current viewing direction
    let dir = new THREE.Vector3(1, 1, 1).normalize();
    if (opts && opts.maintainAngle) {
      dir = cam.position.clone().sub(modelCenter).normalize();
    }
    cam.position.copy(modelCenter.clone().add(dir.multiplyScalar(required)));
    cam.near = required / 100;
    cam.far = required * 100;
    cam.updateProjectionMatrix();
    cam.lookAt(modelCenter);
  }

  function getRequiredDistance(cam, rend, radius) {
    const width = (canvasRoot && canvasRoot.clientWidth) || rootEl.clientWidth || 800;
    const height = (canvasRoot && canvasRoot.clientHeight) || rootEl.clientHeight || 500;
    const vFov = THREE.MathUtils.degToRad(cam.fov);
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * (width / height));
    const distV = radius / Math.tan(vFov / 2);
    const distH = radius / Math.tan(hFov / 2);
    const required = Math.max(distV, distH) * 0.9; // padding
    return required;
  }

  function fadeMeshColor(mesh, fromHex, toHex, durationMs) {
    const from = new THREE.Color(fromHex);
    const to = new THREE.Color(toHex);
    const mat = mesh.material;
    const start = performance.now();
    function step(t) {
      const k = Math.min(1, (t - start) / durationMs);
      mat.color.r = from.r + (to.r - from.r) * k;
      mat.color.g = from.g + (to.g - from.g) * k;
      mat.color.b = from.b + (to.b - from.b) * k;
      // Optional subtle opacity pulse
      mat.opacity = 0.7 + 0.3 * (1 - Math.abs(0.5 - k) * 2);
      if (k < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  // Removed manual height syncing; Swiper autoHeight handles it

  // Page visibility pause/resume
  try {
    document.addEventListener('visibilitychange', function(){
      if (!hasInitialized) return;
      setVisible(!document.hidden && visibilityState);
    });
  } catch (e) {}

  // Optional LocomotiveScroll integration to reinforce visibility checks
  try {
    function locomotiveHook(scroll){
      if (!scroll || typeof scroll.on !== 'function') return;
      let ticking = false;
      scroll.on('scroll', function(){
        if (ticking) return; ticking = true;
        requestAnimationFrame(function(){
          const rect = rootEl.getBoundingClientRect();
          const vh = window.innerHeight || 0;
          const inView = rect.bottom > vh * 0.1 && rect.top < vh * 0.9;
          setVisible(inView);
          ticking = false;
        });
      });
    }
    if (window.wsScroll) locomotiveHook(window.wsScroll);
    window.addEventListener('ws:locomotive-ready', function(e){
      locomotiveHook((e && e.detail && e.detail.scroll) || window.wsScroll);
    }, { once: true });
  } catch (e) {}
}


